// One term's revenue, across the products that earn it.
//
// The feature's unit is the product, so this is the same allocation read
// sideways rather than a second calculation: every figure here is a row that
// already appears under some product. Revenue stays labelled allocated —
// Amazon publishes no per-term figure, and the label travels with the number
// wherever it goes.
import React from 'react';
import { FiX } from 'react-icons/fi';
import s from '../SearchAttribution.module.css';
import { money, num, pct } from '../format';

export default function TermRevenue({ data, loading, onClear }) {
  if (loading) return <div className={s.empty}>Loading…</div>;
  if (!data) return null;

  return (
    <section className={s.panel} style={{ marginBottom: 18 }}>
      <div className={s.contextBar}>
        <div>
          <h4 className={s.expansionTitle} style={{ margin: 0 }}>
            “{data.search_term}”
          </h4>
          <p className={s.hint} style={{ margin: '4px 0 0' }}>
            What this one term is worth, across every product that earns on it.
          </p>
        </div>
        <button className={s.secondary} onClick={onClear}>
          <FiX /> Back to all products
        </button>
      </div>

      {!data.products?.length ? (
        <p className={s.hint} style={{ marginTop: 12 }}>{data.note}</p>
      ) : (
        <>
          <div className={s.figures} style={{ margin: '14px 0' }}>
            <div className={s.figure}>
              <span className={s.figureLabel}>Revenue</span>
              <span className={s.figureValue}>{money(data.revenue)}</span>
              <span className={s.basis}>allocated by us</span>
            </div>
            <div className={s.figure}>
              <span className={s.figureLabel}>Of all search revenue</span>
              <span className={s.figureValue}>
                {pct(data.share_of_account_search_revenue, 1)}
              </span>
            </div>
            <div className={s.figure}>
              <span className={s.figureLabel}>Products earning on it</span>
              <span className={s.figureValue}>{num(data.products_carrying)}</span>
            </div>
            <div className={s.figure}>
              <span className={s.figureLabel}>Purchases</span>
              <span className={s.figureValue}>{num(data.purchases)}</span>
              <span className={s.figureNote}>on {num(data.clicks)} clicks</span>
            </div>
          </div>

          <table className={s.subTable}>
            <thead>
              <tr>
                <th>Product</th>
                <th className={s.right}>Revenue from this term</th>
                <th className={s.right}>Of that product&apos;s search revenue</th>
                <th className={s.right}>Purchases</th>
                <th className={s.right}>Price paid</th>
              </tr>
            </thead>
            <tbody>
              {data.products.map((p) => (
                <tr key={p.asin}>
                  <td>
                    {p.asin}
                    {p.long_tail && <span className={s.tagTail}> long tail</span>}
                  </td>
                  <td className={s.right}>
                    {money(p.revenue)}<span className={s.subFigure}>allocated</span>
                  </td>
                  <td className={s.right}>{pct(p.share_of_its_search_revenue, 0)}</td>
                  <td className={s.right}>{num(p.purchases)}</td>
                  <td className={s.right}>{money(p.median_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className={s.hint} style={{ marginTop: 10 }}>
            A term carrying a tenth of one product and half of another is two different
            findings — the share beside each product is what separates them.
          </p>
        </>
      )}
    </section>
  );
}
